import { useEffect, useRef, useCallback } from 'react';
import { loadEvent as loadEventFromStorage } from '../utils/persistence';
import { mergeTemplateIntoEvent } from '../utils/templateMerge';
import { autoProcessNewResults } from '../utils/autoProcessResults';
import { useEvent } from '../store';
import type { Event } from '../types';

interface UseEventPollingOptions {
  eventId: string | null;
  enabled?: boolean;
  interval?: number; // Polling interval in milliseconds (default: 5000)
  onUpdate?: (event: Event) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for polling event updates
 * 
 * @param options - Polling configuration
 * @returns Object with polling state and controls
 */
export function useEventPolling(options: UseEventPollingOptions) {
  const { eventId, enabled = true, interval = 5000, onUpdate, onError } = options;
  const { event, loadEvent: setEvent } = useEvent();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastEventDataRef = useRef<string | null>(null);

  // Compare event data to detect changes
  const hasEventChanged = useCallback((newEvent: Event | null): boolean => {
    if (!event && !newEvent) return false;
    if (!event || !newEvent) return true;

    // Compare event data as JSON strings
    const currentData = JSON.stringify(event);
    const newData = JSON.stringify(newEvent);

    return currentData !== newData;
  }, [event]);

  // Poll for event updates
  const pollEvent = useCallback(async () => {
    if (!eventId || !enabled) return;

      try {
        let loadedEvent = await loadEventFromStorage(eventId);

        if (!loadedEvent) {
          // Event was deleted, but we'll let the component handle this
          return;
        }

        // CRITICAL: Merge template fights if this is an event instance
        loadedEvent = await mergeTemplateIntoEvent(loadedEvent);

      // Check if event has changed
      if (hasEventChanged(loadedEvent)) {
        // CRITICAL: Auto-process any new results before updating context
        // This ensures bets are resolved when template results propagate
        if (event && !loadedEvent.isTemplate) {
          console.log('[useEventPolling] Checking for new fight results...');
          loadedEvent = await autoProcessNewResults(event, loadedEvent);
        }
        
        // Update event in context
        setEvent(loadedEvent);

        // Call onUpdate callback if provided
        if (onUpdate) {
          onUpdate(loadedEvent);
        }

        // Update last known event data
        lastEventDataRef.current = JSON.stringify(loadedEvent);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to poll event');
      console.error('Error polling event:', err);
      
      if (onError) {
        onError(err);
      }
    }
  }, [eventId, enabled, hasEventChanged, setEvent, onUpdate, onError, event]);

  // Set up polling interval
  useEffect(() => {
    if (!eventId || !enabled) {
      // Clear interval if disabled or no eventId
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial load
    pollEvent();

    // Set up polling interval
    intervalRef.current = setInterval(() => {
      pollEvent();
    }, interval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [eventId, enabled, interval, pollEvent]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Start polling
  const startPolling = useCallback(() => {
    if (!eventId || !enabled) return;
    
    if (!intervalRef.current) {
      pollEvent();
      intervalRef.current = setInterval(() => {
        pollEvent();
      }, interval);
    }
  }, [eventId, enabled, interval, pollEvent]);

  return {
    isPolling: intervalRef.current !== null,
    stopPolling,
    startPolling,
    pollEvent,
  };
}

